import { add, addMonths, sub } from "date-fns";
import { forEach } from "lodash-es";
import isNil from "lodash-es/isNil";
import { makeAutoObservable, runInAction, when } from "mobx";
import { navigate } from "wouter/use-browser-location";
import z from "zod";
import { apiClient, ensureWretchError } from "../../common/apiClient";
import { AvailabilityError, BackendIssueError, ServerNotFoundError } from "../../common/errors";
import { logger } from "../../common/logger";
import { platformRoutes, resolvePlatformRequestUrl } from "../../common/platformRoutes";
import { getLostSalesTotal, sendPurchase } from "../../common/tracking";
import type { BaseStoreType } from "../../common/types/BaseStoreType";
import type {
   DateFilterPeriod,
   DatesTuple,
   DSPOrder,
   DSPOrderIdType,
   DSPOrderList,
   DSPPostOrderResponse,
   M3Order,
   M3OrderListed,
   OrderFilter,
   OrderPlacementResponse,
   PostDSPOrder,
   PostM3Order
} from "../../common/types/m3Types";
import type { DeliveryNotesInfo } from "../../common/types/productOrderTypes";
import {
   type AsyncData,
   initializeWithDefaultData,
   setAsDataAvailable,
   setAsErrorOccured,
   setAsWaitingForData
} from "../../common/utils/asyncDataUtils";
import { formatISODate } from "../../common/utils/dateUtils";
import type { OrderStatusOption } from "../../common/utils/searchOptions";
import { getCartIdFromStorage, setCartIdInStorage } from "../../common/utils/storage/storageUtils";
import {
   type OrderDeliveredMessage,
   type ServerEventMessage,
   ZOrderDeliveredMessage
} from "../../common/zodSchemas/serverEvents";
import theme from "../../themes/theme";
import authStore from "../auth/authStore";
import { getWarehouseId } from "../auth/authUtils";
import availabilityStore from "../availabilityStore";
import cartStore from "../cart/cartStore";
import deliveryDatesStore from "../deliveryDates/deliveryDatesStore";
import productStore from "../product/productStore";
import promotionStore from "../promotionStore";
import serverEventStore from "../serverEventsStore";
import toastStore from "../toastStore";
import { convertOrderCommentStringToObject } from "./orderUtils";
import {
   convertOrderFilterToQueryParams,
   fetchDeliveryNotes,
   fetchOrderDetailsFromM3,
   fetchOrderListFromM3
} from "./orderUtilsAndApi";

// Tracks the id/type the currently loaded dspOrderDetails was fetched with, so that an incoming
// order_delivered event can be matched even before the order has a resolved orderNumber (queue flow).
let currentDSPOrderRequest: { id: string; type: DSPOrderIdType } | null = null;

// The sending-order modal closes with a delay after the order is sent. Callers navigate when the submit resolves, so
// wait for it: on iOS the modal's scroll lock restores the cart's scroll position when it closes, on the next page.
const sendingOrderModalClosed = () => when(() => !cartStore.showSendingOrderModal);

const defaultStart = addMonths(new Date(), -1);
const defaultEnd = addMonths(new Date(), 3);
const INITIAL_FILTER_VALUES: OrderFilter = {
   orderNumber: "",
   orderName: "",
   dateRange: {
      from: defaultStart,
      to: defaultEnd
   },
   status: ""
};

type OrderStoreType = BaseStoreType & {
   orderList: AsyncData<M3OrderListed[]>;
   dspOrderList: AsyncData<DSPOrderList[]>;
   orderDetails: Record<string, AsyncData<M3Order | null>>;
   dspOrderDetails: AsyncData<DSPOrder | null>;
   filters: OrderFilter;
   deliveryNotesInfo: DeliveryNotesInfo | undefined;

   filterAsPickerDates(): DatesTuple;
   updateStatusFilter(option: OrderStatusOption): void;
   updateDateFilter(dates: DatesTuple): void;
   updateOrderNumberFilter(orderNumber: string): void;
   updateDeliveryNotesInfo(orderNumber: string): void;
   ensureOrderDetailsKeyAvailable(orderNumber: string): void;
   getOrderDetails(orderNumber: string): AsyncData<M3Order | null>;
   fetchDSPOrderList(): Promise<void>;
   fetchDSPOrderDetails(id: string, type: DSPOrderIdType): Promise<void>;
   fetchOrderDetails(orderNumber: string): Promise<void>;
   updateOrderListUsingFilters(): Promise<void>;
   deleteOrder(orderNumber: string): Promise<void>;
   submitNewOrderViaQueue(): Promise<DSPPostOrderResponse>;
   submitNewOrder(): Promise<string[]>;
   submitOrderChanges(): Promise<string[]>;
   getPredefinedIntervals(): DateFilterPeriod[];
};

const orderStore: OrderStoreType = makeAutoObservable({
   orderDetails: {},
   orderList: initializeWithDefaultData([]),
   dspOrderList: initializeWithDefaultData([]),
   dspOrderDetails: initializeWithDefaultData(null),
   filters: INITIAL_FILTER_VALUES,
   deliveryNotesInfo: undefined,

   filterAsPickerDates: (): DatesTuple => {
      const res = [orderStore.filters.dateRange.from, orderStore.filters.dateRange.to];
      return res.filter((date): date is Date => date !== null) as DatesTuple;
   },

   updateStatusFilter: (option) => {
      orderStore.filters.status = option.value;
   },

   updateDateFilter: (dates) => {
      orderStore.filters.dateRange.from = dates[0];
      if (dates.length === 2) {
         orderStore.filters.dateRange.to = dates[1];
      } else {
         orderStore.filters.dateRange.to = null;
      }
   },

   updateOrderNumberFilter: (orderNumber: string) => {
      orderStore.filters.orderNumber = orderNumber;
   },

   updateDeliveryNotesInfo: async (orderNumber) => {
      const currentCompany = authStore.currentCompany;
      if (currentCompany && authStore.isLoggedIn()) {
         try {
            const deliveryNotesInfo = await fetchDeliveryNotes(
               orderNumber,
               currentCompany,
               theme.m3CompanyNumber,
               theme.m3DivisionNumber
            );
            if (deliveryNotesInfo?.deliveryNotes.length) {
               orderStore.deliveryNotesInfo = deliveryNotesInfo;
            } else {
               orderStore.deliveryNotesInfo = undefined;
            }
         } catch (_error) {
            orderStore.deliveryNotesInfo = undefined;
         }
      }
   },

   ensureOrderDetailsKeyAvailable: (orderNumber) => {
      if (!(orderNumber in orderStore.orderDetails) || isNil(orderStore.orderDetails[orderNumber])) {
         orderStore.orderDetails[orderNumber] = initializeWithDefaultData(null);
      }
   },

   getOrderDetails: (orderNumber) => {
      orderStore.ensureOrderDetailsKeyAvailable(orderNumber);
      return orderStore.orderDetails[orderNumber];
   },

   fetchDSPOrderList: async () => {
      const customerNumber = authStore.currentCompany;
      if (!authStore.isLoggedIn() || isNil(customerNumber)) {
         logger.log("order", "Searching before logged in");
         return;
      }

      setAsWaitingForData(orderStore.dspOrderList);
      const queryParams = convertOrderFilterToQueryParams(orderStore.filters);
      const customerDSPOrderListUrl = resolvePlatformRequestUrl(platformRoutes.customerDSPOrderList, { storeId: theme.storeId });
      try {
         const result = await apiClient(customerDSPOrderListUrl, authStore.getSessionToken())
            .query({
               customerNumber,
               companyNumber: theme.m3CompanyNumber,
               ...queryParams
            })
            .get()
            .json();
         setAsDataAvailable(orderStore.dspOrderList, result);
      } catch (err) {
         logger.warn("order", "An error occurred while fetching DSP order list: ", err);
         setAsErrorOccured(orderStore.dspOrderList, `${err}`);
      }
   },

   fetchDSPOrderDetails: async (id, type) => {
      const customerNumber = authStore.currentCompany;
      if (!authStore.isLoggedIn() || isNil(id) || isNil(customerNumber)) {
         logger.warn("order", "DSP Order Details before logged in: ", id);
         throw new Error("Not logged in");
      }

      currentDSPOrderRequest = { id, type };
      setAsWaitingForData(orderStore.dspOrderDetails);

      try {
         const url = resolvePlatformRequestUrl(platformRoutes.customerDSPOrderDetails, { id });
         const fetchedOrderDetails = await apiClient(url, authStore.getSessionToken())
            .query({ type, customerNumber, companyNumber: theme.m3CompanyNumber })
            .get()
            .json();
         setAsDataAvailable(orderStore.dspOrderDetails, fetchedOrderDetails);
      } catch (err) {
         const error = ensureWretchError(err);
         if (error?.response.status === 404 || error?.response.status === 403) {
            navigate("/customer/orders");
            toastStore.addError("Ordre ikke funnet", `Ukjent ordrenr ${id}.`, {
               context: "order",
               text: "order_not_found"
            });
         }
         logger.warn("order", "An error occurred while fetching DSP order details: ", err);
         setAsErrorOccured(orderStore.dspOrderDetails, `${err}`);
      }
   },

   fetchOrderDetails: async (orderNumber) => {
      const customerNumber = authStore.currentCompany;
      if (!authStore.isLoggedIn() || isNil(orderNumber) || isNil(customerNumber)) {
         logger.warn("order", "Order Details before logged in: ", orderNumber);
         return;
      }

      orderStore.ensureOrderDetailsKeyAvailable(orderNumber);

      const orderEntry = orderStore.orderDetails[orderNumber];
      setAsWaitingForData(orderEntry);

      try {
         const fetchedOrderDetails = await fetchOrderDetailsFromM3(theme.m3CompanyNumber, customerNumber, orderNumber);
         if ("orderNumber" in fetchedOrderDetails) {
            const commentObject = convertOrderCommentStringToObject(fetchedOrderDetails.orderComment);

            const orderDetails = { ...fetchedOrderDetails };
            forEach(orderDetails.optimizedOrderLines, (orderLine) => {
               if (commentObject[orderLine.sku]) {
                  orderLine.appliedPromotionInformation = commentObject[orderLine.sku];
               }
            });

            setAsDataAvailable(orderEntry, orderDetails);
         } else if ("Error_Code" in fetchedOrderDetails) {
            logger.warn("order", "An error occurred while fetching order: ", fetchedOrderDetails.Error_Code);
            setAsErrorOccured(orderEntry, fetchedOrderDetails.Response);
         }
      } catch (err) {
         logger.warn("order", "An error occurred while fetching order: ", err);
         setAsErrorOccured(orderEntry, `${err}`);
      }
   },

   deleteOrder: async (orderNumber) => {
      if (!authStore.isLoggedIn() || isNil(orderNumber)) {
         logger.warn("order", "Unable to delete order, order number is missing");
         return;
      }

      const customerOrderUrl = resolvePlatformRequestUrl(platformRoutes.customerOrder, { storeId: theme.storeId });

      return apiClient(customerOrderUrl, authStore.getSessionToken())
         .query({
            companyNumber: theme.m3CompanyNumber,
            customerNumber: authStore.currentCompany,
            orderNumber,
            typeOfOrder: "K"
         })
         .delete()
         .json((res) => {
            if (res.status !== true) {
               throw res.Error_Code;
            }
            void orderStore.fetchOrderDetails(orderNumber);
         });
   },

   updateOrderListUsingFilters: async () => {
      const customerNumber = authStore.currentCompany;
      if (!authStore.isLoggedIn() || isNil(customerNumber)) {
         logger.log("order", "Searching before logged in");
         return;
      }

      logger.log("order", `Fetching orders for customer ${customerNumber}`);

      setAsWaitingForData(orderStore.orderList);

      try {
         const orders: M3OrderListed[] = await fetchOrderListFromM3(theme.m3CompanyNumber, customerNumber, orderStore.filters);
         logger.log("order", "fetchOrderListWithFilters: ", orders);
         setAsDataAvailable(orderStore.orderList, orders);
      } catch (err) {
         setAsErrorOccured(orderStore.orderList, `${err}`);
      }
   },

   submitNewOrderViaQueue: async () => {
      cartStore.setSendingOrder(true);
      let response: DSPPostOrderResponse;
      try {
         runInAction(() => {
            availabilityStore.availabilityFetchProgress = null;
         });

         let availabilityChecked = false;
         try {
            const { allAvailable, checked } = await availabilityStore.checkAvailability();

            if (!allAvailable) {
               logger.log("order", "Aborting order placement to resolve availability issues");
               cartStore.setSendingOrder(false, "closeImmediately");

               throw new AvailabilityError();
            }
            availabilityChecked = checked;
         } catch (err) {
            if (err instanceof ServerNotFoundError) {
               logger.log("order", "availability service not available, continue sending order");
               runInAction(() => {
                  availabilityStore.clearAvailability();
               });
            } else {
               throw err;
            }
         }

         const currentDelivery = deliveryDatesStore.getCurrentDelivery();
         if (isNil(currentDelivery)) {
            throw new BackendIssueError("Delivery day not available");
         }

         const orderLines = [
            ...cartStore.items.map((i) => ({
               sku: i.sku,
               quantity: i.qty,
               orderLineUnit: i.unit,
               price: productStore.resolveSku(i.sku)?.price
            })),
            ...cartStore.lostSales
         ];

         // Snapshot the cart values used for tracking at payload-build time (before the awaited request)
         // so tracking reflects exactly what we submitted, even if the cart mutates during the request.
         const cartItems = cartStore.items.map((item) => ({ ...item }));
         const appliedPromotionIdsBySku = promotionStore.getAppliedPromotionIdsBySku(cartItems.map(({ sku }) => sku));
         const lostSalesTotal = getLostSalesTotal(cartStore.lostSales);
         const cartTotal = cartStore.getCartTotal().total;
         const cartOrderType = cartStore.orderType;

         const promotionIds = promotionStore.availablePromotions.data.map((p) => p.id);

         const cartId = getCartIdFromStorage();
         setCartIdInStorage(cartId);

         const order: PostDSPOrder = {
            companyNumber: theme.m3CompanyNumber,
            customerNumber: authStore.currentCompany || "",
            orderType: cartOrderType,
            customerWarehouse: getWarehouseId(authStore.getCurrentCompany()),
            requestedDeliveryDate: formatISODate(currentDelivery.date),
            estimatedOrderAmount: 2500,
            promotionIds,
            ...cartStore.orderRefs,
            orderLines,
            cartId,
            availabilityChecked
         };

         logger.log("order", "Starting order placement...");

         const url = resolvePlatformRequestUrl(platformRoutes.customerDSPOrderPost, {});
         response = await apiClient(url, authStore.getSessionToken())
            .query({
               optimizeOrderLines: true
            })
            .content("application/json")
            .post(order)
            .json();

         try {
            sendPurchase(
               response.messageId,
               cartItems,
               false,
               cartTotal,
               cartOrderType,
               lostSalesTotal,
               appliedPromotionIdsBySku
            );
         } catch (trackingErr) {
            logger.warn("order", "Failed to send purchase tracking for queued order", trackingErr);
         }
      } catch (err) {
         if (!(err instanceof AvailabilityError)) {
            logger.warn("order", "An unexpected error occurred during availability check", err);
         }
         throw err;
      } finally {
         cartStore.setSendingOrder(false);
      }

      cartStore.emptyCart();
      deliveryDatesStore.selectNextDeliveryDateForOrderType(cartStore.orderType);
      await sendingOrderModalClosed();

      return response;
   },

   submitNewOrder: async () => {
      cartStore.setSendingOrder(true);
      let response: OrderPlacementResponse[];
      try {
         runInAction(() => {
            availabilityStore.availabilityFetchProgress = null;
         });

         try {
            const { allAvailable } = await availabilityStore.checkAvailability();

            if (!allAvailable) {
               logger.log("order", "Aborting order placement to resolve availability issues");
               cartStore.setSendingOrder(false, "closeImmediately");

               throw new AvailabilityError();
            }
         } catch (err) {
            if (err instanceof ServerNotFoundError) {
               logger.log("order", "availability service not available, continue sending order");
               runInAction(() => {
                  availabilityStore.clearAvailability();
               });
            } else {
               throw err;
            }
         }

         const currentDelivery = deliveryDatesStore.getCurrentDelivery();
         if (isNil(currentDelivery)) {
            throw new BackendIssueError("Delivery day not available");
         }

         const orderLines = [
            ...cartStore.items.map((i) => ({
               sku: i.sku,
               quantity: i.qty,
               orderLineUnit: i.unit,
               price: productStore.resolveSku(i.sku)?.price
            })),
            ...cartStore.lostSales
         ];

         const promotionIds = promotionStore.availablePromotions.data.map((p) => p.id);

         const cartId = getCartIdFromStorage();
         setCartIdInStorage(cartId);

         const order: PostM3Order = {
            companyNumber: theme.m3CompanyNumber,
            customerNumber: authStore.currentCompany || "",
            orderType: cartStore.orderType,
            customerWarehouse: getWarehouseId(authStore.getCurrentCompany()),
            requestedDeliveryDate: formatISODate(currentDelivery.date),
            estimatedOrderAmount: 2500,
            promotionIds,
            ...cartStore.orderRefs,
            orderLines,
            cartId
         };

         const cartItems = cartStore.items.map((item) => ({ ...item }));
         const appliedPromotionIdsBySku = promotionStore.getAppliedPromotionIdsBySku(cartItems.map(({ sku }) => sku));
         const lostSalesTotal = getLostSalesTotal(cartStore.lostSales);
         const cartTotal = cartStore.getCartTotal().total;
         const cartOrderType = cartStore.orderType;

         logger.log("order", "Starting order placement...");

         const customerOrderUrl = resolvePlatformRequestUrl(platformRoutes.customerOrder, { storeId: theme.storeId });

         response = await apiClient(customerOrderUrl, authStore.getSessionToken())
            .query({
               optimizeOrderLines: true
            })
            .content("application/json")
            .post(order)
            .json();

         sendPurchase(
            response.map((o) => o.orderNumber),
            cartItems,
            false,
            cartTotal,
            cartOrderType,
            lostSalesTotal,
            appliedPromotionIdsBySku
         );
         if (cartStore.editing) {
            const { orderNumber } = cartStore.editing;
            if (orderNumber && orderNumber !== response[0].orderNumber) {
               runInAction(() => {
                  cartStore.orderRefs.replacesOrderNumber = orderNumber;
               });

               await orderStore.deleteOrder(orderNumber);
            }
         }
         logger.log("order", "Order submit response:", response);
      } catch (err) {
         if (!(err instanceof AvailabilityError)) {
            logger.warn("order", "An unexpected error occurred during availability check", err);
         }
         throw err;
      } finally {
         cartStore.setSendingOrder(false);
      }

      cartStore.emptyCart();
      deliveryDatesStore.selectNextDeliveryDateForOrderType(cartStore.orderType);
      await sendingOrderModalClosed();

      return response.map((o) => o.orderNumber);
   },

   submitOrderChanges: async () => {
      if (isNil(cartStore.editing) || isNil(cartStore.editing.orderNumber)) {
         logger.warn("order", "Cannot send order change when editing is nil");
         toastStore.addError("Uforutsett feil oppsto", "Vi greier ikke å sende inn ordren din.", {
            context: "order",
            text: "change_order_edit_mode_error"
         });
         return Promise.reject();
      }
      if (cartStore.editing.isGeneratedSubscriptionOrder && cartStore.isDeliveryDateChanged()) {
         toastStore.addError("Kan ikke endre ordren", "Leveringsdatoen må være den samme som på ordren.");
         throw new Error("Cannot change the delivery date of a generated subscription order");
      }
      cartStore.setSendingOrder(true);

      runInAction(() => {
         availabilityStore.availabilityFetchProgress = null;
      });
      // Legacy M3 change-order flow must block submit when availability service/check is unavailable.
      const { allAvailable } = await availabilityStore.checkAvailability();

      if (!allAvailable) {
         logger.log("order", "Aborting order placement to resolve availability issues");
         cartStore.setSendingOrder(false, "closeImmediately");

         throw new AvailabilityError();
      }

      if (cartStore.isDeliveryDateChanged()) {
         const result = await orderStore.submitNewOrder();

         void cartStore.stopEditOrderMode();
         return result;
      }

      const payload = cartStore.generateChangeOrderPayload();

      if (payload === null) {
         cartStore.setSendingOrder(false, "closeImmediately");
         return [cartStore.editing.orderNumber];
      }

      let response: OrderPlacementResponse[];
      const cartItems = cartStore.items.map((item) => ({ ...item }));
      const lostSalesTotal = getLostSalesTotal(cartStore.lostSales);
      const cartTotal = cartStore.getCartTotal().total;
      const cartOrderType = cartStore.orderType;
      try {
         const customerOrderUrl = resolvePlatformRequestUrl(platformRoutes.customerOrder, { storeId: theme.storeId });

         response = await apiClient(customerOrderUrl, authStore.getSessionToken())
            .query({
               optimizeOrderLines: true
            })
            .content("application/json")
            .put(payload)
            .json();
         runInAction(() => {
            // Remove the cached order details so we are able to see the changes load in on the confirmation page
            if (cartStore.editing?.orderNumber) {
               delete orderStore.orderDetails[cartStore.editing.orderNumber];
            }
         });

         void cartStore.stopEditOrderMode();
         sendPurchase(
            response.map((o) => o.orderNumber),
            cartItems,
            true,
            cartTotal,
            cartOrderType,
            lostSalesTotal
         );
      } catch (err) {
         logger.warn("order", "An error occurred during order update", err);
         throw err;
      } finally {
         cartStore.setSendingOrder(false);
      }
      await sendingOrderModalClosed();

      return response.map((o) => o.orderNumber);
   },
   getPredefinedIntervals: () => {
      const now = new Date();
      const oneMonthLater = add(now, { months: 1 });
      return [
         {
            value: [sub(now, { months: 1 }), oneMonthLater],
            text: "Siste måneden"
         },
         {
            value: [sub(now, { months: 3 }), oneMonthLater],
            text: "Siste tre måneder"
         },
         {
            value: [sub(now, { months: 6 }), oneMonthLater],
            text: "Siste halvåret"
         },
         {
            value: [sub(now, { years: 1 }), oneMonthLater],
            text: "Siste året"
         }
      ];
   },
   clearCompanySpecificData: () => {
      // add methods to clear company specific data
      orderStore.orderList = initializeWithDefaultData([]);
      orderStore.orderDetails = {};
      orderStore.dspOrderDetails = initializeWithDefaultData(null);
      orderStore.filters = INITIAL_FILTER_VALUES;
      orderStore.deliveryNotesInfo = undefined;
      currentDSPOrderRequest = null;
   }
} satisfies OrderStoreType);

// Registered once at module load so it is always active while the websocket is connected, independent
// of whether the order details view is currently mounted. When the order the user is viewing (or the
// queued order they just submitted) is delivered, refresh dspOrderDetails in place; the observer view
// re-renders automatically when mounted, and the store already holds fresh data for the next mount.
const handleOrderDelivered = (message: ServerEventMessage) => {
   const { success } = z.safeParse(ZOrderDeliveredMessage, message);
   if (!success) {
      return;
   }
   const { orderNumber, messageId } = message as OrderDeliveredMessage;
   const currentOrderNumber = orderStore.dspOrderDetails.data?.orderNumber;
   // Match the event to whatever the user is currently viewing using the right identifier for the flow:
   // - matchesQueueRequest: queue flow, where the order has no orderNumber yet, so we compare the event's
   //   messageId against the id we fetched with (only when that fetch used type "queueMessageId").
   // - matchesLoadedOrder: a resolved order is loaded, so we compare the event's orderNumber against it.
   // We only ignore the event when BOTH miss; a single match is enough to trigger a refresh.
   const matchesQueueRequest = currentDSPOrderRequest?.type === "queueMessageId" && currentDSPOrderRequest.id === messageId;
   const matchesLoadedOrder = !!currentOrderNumber && currentOrderNumber === orderNumber;
   if (!matchesQueueRequest && !matchesLoadedOrder) {
      logger.log(
         "websockets",
         `Received order_delivered event for order ${orderNumber}, but it is not the currently loaded order. Ignoring.`
      );
      return;
   }
   logger.log("websockets", `Received order_delivered event for order ${orderNumber}, refreshing dsp order details.`);
   void orderStore.fetchDSPOrderDetails(orderNumber, "orderNumber");
};

serverEventStore.addListener("order_delivered", handleOrderDelivered);

export default orderStore;
